/**
 * IR-1548: the statistics band above the sessions table.
 *
 * The table answers "what happened in this session". The band answers "where
 * is the problem", which is a question about all ~138 sessions rather than the
 * fifty on screen — so it reads a separate, deliberately thin endpoint
 * (`/sessions/overview`) that returns the whole history four fields wide.
 *
 * ONE DATASET DRIVES ALL THREE CHARTS. The ribbon, the mix bars and the
 * headline are computed by a single pass in `summarise`, so they cannot quote
 * totals that disagree — the usual way a summary tile and the chart beneath it
 * end up contradicting each other.
 *
 * Outcome is a STATE, so it takes the reserved status palette (green / blue /
 * orange / grey) rather than a categorical set, and every mark is also named
 * in words in the legend — nothing here is encoded by colour alone.
 *
 * ⚠️ The band is ALWAYS both environments, even when the table's env filter is
 * set to one. Its entire subject is the difference between them, so filtering
 * it would leave the divergence headline with nothing to compare. It says so
 * on screen rather than silently contradicting the rows beneath it.
 */
import { useMemo } from "react";

export type OverviewSession = {
  cycle_id: string;
  env: string;
  /** ISO-8601 session START, parsed server-side from the folder name. Null
   *  when the name is not a stamp — such a session cannot be placed on the
   *  time axis, and the endpoint counts it in `undated`. */
  ts: string | null;
  outcome: string;
};

export type OutcomeKey = "data" | "empty" | "miss" | "unknown" | "absent";

/** Fixed order, never cycled — the legend, the bars and the lanes all use it. */
const OUTCOME_ORDER: OutcomeKey[] = ["data", "empty", "miss", "unknown", "absent"];

const OUTCOME_LABEL: Record<OutcomeKey, string> = {
  data: "data",
  empty: "visited, empty",
  miss: "attempted, no data",
  // IR-1753 r8: Rotem's matrix name (Unknown) plus her reason (attempt unknown).
  unknown: "unknown (attempt unknown)",
  absent: "not attempted",
};

/** Folders are deleted at 30 days in both archives — the same constant the
 *  backend's `is_retrievable` applies, restated here because this figure is
 *  derived client-side from `ts` rather than fetched. */
const RETENTION_MS = 30 * 24 * 3600_000;

/** The session index begins here — the same fact the page states above the
 *  table ("The index begins 2026-09-03"). Sessions stamped earlier exist only
 *  because they were delivered late; the ribbon's axis starts here and counts
 *  them at the left edge rather than stretching back to reach them. */
const INDEX_START = Date.parse("2026-09-03T00:00:00Z");

export type Counts = Record<OutcomeKey, number> & { total: number };

export type Summary = {
  /** The de-duplicated rows the charts are drawn from. Everything below is
   *  derived from THIS list, which is what keeps the three charts honest. */
  sessions: (OverviewSession & { at: number; key: OutcomeKey })[];
  prod: Counts;
  dev: Counts;
  /** The shared scale for both mix bars: the larger environment's total. */
  scale: number;
  total: number;
  paired: number;
  differing: number;
  prodOnly: number;
  devOnly: number;
  openable: number;
  /** Where the ribbon's axis starts: the index start when some sessions
   *  predate it and some do not, otherwise the earliest session. */
  from: number | null;
  to: number | null;
  /** The earliest session start, before any clamping. */
  earliest: number | null;
  /** Sessions that started before `from`, per lane — drawn as a counted
   *  marker at the left edge, not as ticks. Zero unless the axis is clamped. */
  early: { prod: number; dev: number };
  /** Contiguous runs of cycles prod collected and dev did not, longest first. */
  gaps: { from: number; to: number; count: number }[];
};

function emptyCounts(): Counts {
  return { data: 0, empty: 0, miss: 0, unknown: 0, absent: 0, total: 0 };
}

function toKey(outcome: string): OutcomeKey {
  // IR-1753 r8: the backend's `_outcome()` now returns 'unknown' when
  // `attempted` is NULL (whether a read started is not known). That is its own
  // key: folding it into "absent" would say "never tried", which is a claim the
  // data does not make.
  //
  // The floor for a word this client has never seen is "unknown", NOT
  // "absent". It still keeps the parts summing to the total, but it no longer
  // reads as the one state a new word is least likely to mean: a word we cannot
  // place is, by definition, one we do not know.
  return (OUTCOME_ORDER as string[]).includes(outcome) ? (outcome as OutcomeKey) : "unknown";
}

export function summarise(rows: OverviewSession[], now: number = Date.now()): Summary {
  // ⚠️ De-duplicated on (env, cycle_id). `ops_group_collection_outcome` is not
  // uniquely constrained on (group, cycle) — `session_row()` orders by id DESC
  // and takes one, which is an admission that duplicates occur. Counted twice,
  // the ribbon would draw two ticks where the headline counts one session.
  const seen = new Map<string, OverviewSession & { at: number; key: OutcomeKey }>();
  for (const r of rows) {
    const at = r.ts ? Date.parse(r.ts) : Number.NaN;
    if (Number.isNaN(at)) continue;
    const id = `${r.env}|${r.cycle_id}`;
    if (!seen.has(id)) seen.set(id, { ...r, at, key: toKey(r.outcome) });
  }
  const sessions = [...seen.values()].sort((a, b) => a.at - b.at);

  const prod = emptyCounts();
  const dev = emptyCounts();
  let openable = 0;
  for (const x of sessions) {
    const c = x.env === "dev" ? dev : prod;
    c[x.key] += 1;
    c.total += 1;
    if (now - x.at <= RETENTION_MS) openable += 1;
  }

  // Cycles, in time order, with which environments ran them. The pairing, the
  // divergence count and the gap runs all read from this one index.
  const cycles = new Map<string, { at: number; prod?: OutcomeKey; dev?: OutcomeKey }>();
  for (const x of sessions) {
    const c = cycles.get(x.cycle_id) ?? { at: x.at };
    c.at = Math.min(c.at, x.at);
    if (x.env === "dev") c.dev = x.key;
    else c.prod = x.key;
    cycles.set(x.cycle_id, c);
  }
  const ordered = [...cycles.values()].sort((a, b) => a.at - b.at);

  let paired = 0;
  let differing = 0;
  let prodOnly = 0;
  let devOnly = 0;
  for (const c of ordered) {
    if (c.prod !== undefined && c.dev !== undefined) {
      paired += 1;
      // Only a PAIRED cycle can differ. An unpaired one is a gap, counted
      // under its own heading — folding it in here would report the same
      // failure twice under two names.
      if (c.prod !== c.dev) differing += 1;
    } else if (c.prod !== undefined) prodOnly += 1;
    else if (c.dev !== undefined) devOnly += 1;
  }

  // A RUN of prod-only cycles is a different fact from one missing session:
  // it says dev stopped collecting for a while. A single hole is already
  // visible in the lane, so only runs of two or more are drawn.
  const gaps: { from: number; to: number; count: number }[] = [];
  let run: { from: number; to: number; count: number } | null = null;
  for (const c of ordered) {
    const isGap = c.prod !== undefined && c.dev === undefined;
    if (isGap) {
      run = run
        ? { from: run.from, to: c.at, count: run.count + 1 }
        : { from: c.at, to: c.at, count: 1 };
    } else if (run) {
      if (run.count > 1) gaps.push(run);
      run = null;
    }
  }
  if (run && run.count > 1) gaps.push(run);
  gaps.sort((a, b) => b.count - a.count);

  // Yaniv's choice (a), 2026-09-13. A handful of sessions were collected
  // before the index began and delivered days later (dev: 192 of 74,138 rows,
  // 124 groups; e.g. folder 08-28, ingested 09-04). Plotted at their start,
  // one of them stretched the axis back a week, squeezed every other session
  // into the right two-thirds, and left an empty stretch that read like a
  // collection outage. Clamp only when there is something after the index to
  // show — a group whose sessions ALL predate it keeps its own span.
  const earliest = sessions.length ? (sessions[0]?.at ?? null) : null;
  const latest = sessions.length ? (sessions[sessions.length - 1]?.at ?? null) : null;
  const clamp =
    earliest !== null && latest !== null && earliest < INDEX_START && latest > INDEX_START;
  const from = clamp ? INDEX_START : earliest;
  const early = { prod: 0, dev: 0 };
  if (clamp) {
    for (const x of sessions) {
      if (x.at < INDEX_START) early[x.env === "dev" ? "dev" : "prod"] += 1;
    }
  }

  return {
    sessions,
    prod,
    dev,
    scale: Math.max(prod.total, dev.total, 1),
    total: sessions.length,
    paired,
    differing,
    prodOnly,
    devOnly,
    openable,
    from,
    to: latest,
    earliest,
    early,
    gaps,
  };
}

/**
 * Where a session sits along the lane, 0–100.
 *
 * 🪤 A zero-width span — one session, or several written in the same second —
 * divides by zero. `left: NaN%` is not an error the browser reports: the
 * declaration is dropped and every tick stacks at the left edge, which reads
 * as a real burst of activity. Centre them instead.
 */
export function tickPercent(at: number, from: number, to: number): number {
  if (!(to > from)) return 50;
  return Math.min(100, Math.max(0, ((at - from) / (to - from)) * 100));
}

function stamp(t: number, withTime: boolean): string {
  const d = new Date(t);
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  if (!withTime) return `${mm}-${dd}`;
  const hh = String(d.getUTCHours()).padStart(2, "0");
  const mi = String(d.getUTCMinutes()).padStart(2, "0");
  return `${mm}-${dd} ${hh}:${mi}`;
}

/** Four evenly-spaced labels. Every one names a time the ribbon actually
 *  reaches, and the outer two are aligned inward so they stay inside the
 *  lane's width rather than being clipped by the panel edge. */
function axisTicks(from: number, to: number) {
  if (!(to > from)) return [];
  const withTime = to - from < 3 * 24 * 3600_000;
  return [0, 1, 2, 3].map((i) => {
    const pct = (i / 3) * 100;
    return {
      pct,
      label: stamp(from + ((to - from) * i) / 3, withTime),
      align: i === 0 ? "left" : i === 3 ? "right" : "center",
    };
  });
}

function Lane({
  env,
  summary,
  read,
  compared,
}: {
  env: "prod" | "dev";
  summary: Summary;
  /** Did this server read this environment at all? */
  read: boolean;
  /** Were BOTH read — i.e. is a gap between them a measurement? */
  compared: boolean;
}) {
  const { from, to } = summary;
  const label = env.toUpperCase();
  return (
    <div className="iris-band-lane-row">
      <span className={`iris-band-lane-label iris-band-lane-label--${env}`}>{label}</span>
      <div className="iris-band-lane" data-lane={env}>
        {/* An environment this server never read is NOT an environment that
            collected nothing. Empty lanes look identical either way, so the
            unread one says which it is in words. */}
        {!read && <span className="iris-band-lane-unread">not readable from this server</span>}
        {summary.sessions
          .filter((x) => (env === "dev" ? x.env === "dev" : x.env !== "dev"))
          // Sessions before the axis start are COUNTED by the edge marker
          // below, not drawn — a tick pinned to 0% would stack on the marker
          // and claim a position the axis does not show.
          .filter((x) => from === null || x.at >= from)
          .map((x) => (
            <span
              key={`${x.env}-${x.cycle_id}`}
              data-tick={x.key}
              className={`iris-band-tick iris-band-tick--${x.key}`}
              style={{ left: `${tickPercent(x.at, from ?? 0, to ?? 0)}%` }}
              title={`${label}  ${stamp(x.at, true)}  ·  ${OUTCOME_LABEL[x.key]}`}
            />
          ))}
        {summary.early[env] > 0 && (
          <span
            data-early-marker={summary.early[env]}
            className="iris-band-early"
            title={`${summary.early[env]} ${label} session${summary.early[env] === 1 ? "" : "s"} started before the index began on ${stamp(from ?? 0, false)} (earliest ${stamp(summary.earliest ?? 0, false)})`}
          >
            ‹{summary.early[env]}
          </span>
        )}
        {env === "dev" &&
          compared &&
          summary.gaps
            .filter((g) => from === null || g.to >= from)
            .map((g) => (
              <span
                key={`gap-${g.from}`}
                data-gap-zone
                className="iris-band-gap-zone"
                style={{
                  left: `${tickPercent(g.from, from ?? 0, to ?? 0)}%`,
                  width: `${Math.max(
                    0.6,
                    tickPercent(g.to, from ?? 0, to ?? 0) - tickPercent(g.from, from ?? 0, to ?? 0),
                  )}%`,
                }}
                title={`No dev session between ${stamp(g.from, true)} and ${stamp(g.to, true)}`}
              />
            ))}
      </div>
    </div>
  );
}

function MixBar({
  env,
  counts,
  scale,
  read,
}: {
  env: "prod" | "dev";
  counts: Counts;
  scale: number;
  read: boolean;
}) {
  return (
    <div className="iris-band-mix-row">
      <span className={`iris-band-mix-label iris-band-mix-label--${env}`}>{env.toUpperCase()}</span>
      <div>
        <div className="iris-band-mix-bar" data-mix={env}>
          {OUTCOME_ORDER.filter((k) => counts[k] > 0).map((k) => (
            <span
              key={k}
              data-seg={k}
              className={`iris-band-seg iris-band-seg--${k}`}
              // Both bars divide by the SAME scale — the larger environment's
              // total — so a shorter bar means fewer sessions. Normalising
              // each to its own total would hide exactly what we are hunting.
              style={{ width: `${(counts[k] / scale) * 100}%` }}
              title={`${OUTCOME_LABEL[k]} — ${counts[k]} sessions`}
            />
          ))}
        </div>
        <div className="iris-band-mix-numbers" data-mix-numbers={env}>
          {!read
            ? "not readable from this server"
            : counts.total === 0
              ? "no sessions"
              : OUTCOME_ORDER.filter((k) => counts[k] > 0).map((k, i) => (
                  <span key={k}>
                    {i > 0 ? " · " : ""}
                    <b>{counts[k]}</b> {OUTCOME_LABEL[k]}
                  </span>
                ))}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, id }: { label: string; value: React.ReactNode; id: string }) {
  return (
    <div className="iris-band-stat">
      <span>{label}</span>
      <b data-testid={id}>{value}</b>
    </div>
  );
}

export function SessionsBand({
  sessions,
  truncated = false,
  envsRead,
  undated = 0,
  now,
}: {
  sessions: OverviewSession[];
  truncated?: boolean;
  /**
   * Which environments the server actually READ — the endpoint's `envs_read`.
   *
   * ⚠️ The merged read SKIPS an environment it cannot open: the dev VM has no
   * `dev` alias unless OPS_DEV_DB_HOST is set, and on prod the dev connection
   * can fail. Drawn without this, every prod session becomes "collected in
   * prod with no dev counterpart" — a missing READING rendered as missing
   * SESSIONS, the exact confusion this page exists to prevent. Absent means
   * the caller did not say, and both are assumed read.
   */
  envsRead?: string[] | undefined;
  /** Sessions the server could not date (unparseable folder name). They are
   *  not drawn, so the band says how many it left off. */
  undated?: number | undefined;
  /** Injectable so the retention figure is testable without freezing time. */
  now?: number;
}) {
  const s = useMemo(() => summarise(sessions, now ?? Date.now()), [sessions, now]);
  const axis = axisTicks(s.from ?? 0, s.to ?? 0);
  const readProd = !envsRead || envsRead.includes("prod");
  const readDev = !envsRead || envsRead.includes("dev");
  // Every comparison figure — pairs, gaps, one-sided counts, the headline — is
  // a claim about BOTH environments, so it exists only when both were read.
  const compared = readProd && readDev;
  const gap = compared ? s.gaps[0] : undefined;
  const onlyRead = readProd ? "prod" : "dev";
  const dash = "—";

  return (
    <div className="iris-band">
      <section>
        <p className="iris-band-head">
          Coverage <span>every session on the index, prod above dev</span>
        </p>

        {s.total === 0 ? (
          <p className="iris-band-empty">
            No sessions on the index for this group. The index begins 2026-09-03.
          </p>
        ) : (
          <>
            <div>
              <Lane env="prod" summary={s} read={readProd} compared={compared} />
              <Lane env="dev" summary={s} read={readDev} compared={compared} />
              <div className="iris-band-lane-row">
                <span />
                <div className="iris-band-axis">
                  {axis.map((t) => (
                    <span key={t.pct} style={{ left: `${t.pct}%` }} data-align={t.align}>
                      {t.label}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {s.early.prod + s.early.dev > 0 && (
              <p className="iris-band-early-note">
                {s.early.prod + s.early.dev} sessions started before the index began on{" "}
                {stamp(s.from ?? 0, false)} (earliest {stamp(s.earliest ?? 0, false)}) — counted at
                the left edge, not drawn to scale.
              </p>
            )}

            {gap && (
              <p className="iris-band-gap-note">
                {gap.count} sessions collected in prod with no dev counterpart,{" "}
                {stamp(gap.from, true)} to {stamp(gap.to, true)}
              </p>
            )}

            <div className="iris-band-legend" data-legend>
              {OUTCOME_ORDER.map((k) => (
                <i key={k} className={`iris-band-key iris-band-key--${k}`}>
                  <span>{OUTCOME_LABEL[k]}</span>
                </i>
              ))}
            </div>

            <p className="iris-band-head iris-band-head--spaced">
              Outcome mix <span>both bars on one scale</span>
            </p>
            <MixBar env="prod" counts={s.prod} scale={s.scale} read={readProd} />
            <MixBar env="dev" counts={s.dev} scale={s.scale} read={readDev} />
          </>
        )}

        {undated > 0 && (
          <p className="iris-band-truncated">
            {undated} sessions have no readable start time in their folder name and are not drawn on
            this chart.
          </p>
        )}

        {truncated && (
          <p className="iris-band-truncated">
            More sessions exist than this chart reads — what you see is the most recent slice, not
            the whole history.
          </p>
        )}
      </section>

      <section>
        <p className="iris-band-head">Prod vs dev</p>
        <div className="iris-band-headline">
          <em
            data-testid="headline-differing"
            data-zero={!compared || s.differing === 0 ? "true" : "false"}
          >
            {compared ? s.differing : dash}
          </em>{" "}
          <span>
            of <span data-testid="headline-paired">{compared ? s.paired : dash}</span> paired
            sessions differ
          </span>
        </div>
        {/* Says what it MEASURES. The mockup's caption promised "different
            outcome, or media present on one side only" — media needs a GCS
            listing per session, which this dataset does not carry, so that
            caption would have overclaimed on every group. The media
            comparison lives in the expanded panel, where the listing is. */}
        {compared ? (
          <p className="iris-band-headsub">
            A cycle both environments ran where the recorded outcome does not agree. Media is
            compared per session, in the panel below.
          </p>
        ) : (
          <p className="iris-band-headsub">
            Only {onlyRead} was readable from this server, so there is nothing to compare it
            against. The figures below describe {onlyRead} alone.
          </p>
        )}
        <div className="iris-band-rule" />
        <Stat id="stat-total" label="Sessions on the index" value={s.total} />
        <Stat
          id="stat-prod-only"
          label="Prod only, no dev twin"
          value={compared ? s.prodOnly : dash}
        />
        <Stat
          id="stat-dev-only"
          label="Dev only, no prod twin"
          value={compared ? s.devOnly : dash}
        />
        <Stat
          id="stat-openable"
          label="Openable (within 30 days)"
          value={`${s.openable} of ${s.total}`}
        />
      </section>
    </div>
  );
}
